import React, { useEffect, useState } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { QuoteForm } from "@/components/QuoteForm";
import { blankField, LockedQuestions, QuestionsEditor } from "@/components/FieldEditor";
import { PageHead, Panel } from "@/pages/vendor/Shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

function useBaseTypes() {
  const { vendor } = useAuth();
  const [types, setTypes] = useState<any[]>([]);
  const [typeId, setTypeId] = useState("");
  useEffect(() => {
    api.get("/event-types").then((r) => {
      const typical: string[] = vendor?.vendor?.typical_event_types || [];
      const sorted = [...r.data].sort((a, b) => Number(typical.includes(b.id)) - Number(typical.includes(a.id)));
      setTypes(sorted);
      setTypeId(sorted[0]?.id || "");
    });
  }, [vendor]);
  return { types, typeId, setTypeId, current: types.find((t) => t.id === typeId) };
}

export default function Forms() {
  const [forms, setForms] = useState<any[]>([]);
  const [cur, setCur] = useState<any>(null);
  const base = useBaseTypes();
  const load = async (selectId?: string) => {
    const { data } = await api.get("/vendor/forms");
    setForms(data);
    setCur(data.find((f: any) => f.id === selectId) || data[0] || null);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const { data } = cur.id ? await api.put(`/vendor/forms/${cur.id}`, cur) : await api.post("/vendor/forms", cur);
      toast.success("Questions saved");
      load(data.id);
    } catch (e: any) { toast.error(e?.response?.status === 402 ? "Extra questions are included in the Pro plan" : errMsg(e)); }
  };
  const remove = async () => {
    if (cur.id) await api.delete(`/vendor/forms/${cur.id}`);
    load();
  };

  return (
    <div>
      <PageHead eyebrow="Booth questions" title="Quote forms & extra questions">
        <Button variant="secondary" onClick={() => setCur({ name: "New question set", fields: [blankField()] })} data-testid="forms-new-button"><Plus className="mr-1 h-4 w-4" /> New question set</Button>
      </PageHead>
      <p className="mb-5 max-w-2xl text-sm text-muted-foreground">Every booth asks its event type's standard questions automatically. Attendees answer those once and share them with every booth they scan. Add your own extra questions here and attach a set to each event.</p>
      <div className="mb-4 flex flex-wrap gap-2">
        {forms.map((f) => (
          <button key={f.id} onClick={() => setCur(f)} className={`rounded-sm border px-3 py-1.5 text-sm ${cur?.id === f.id ? "border-emerald-500 text-emerald-700" : "border-border text-muted-foreground"}`} data-testid={`forms-tab-${f.id}`}>{f.name}</button>
        ))}
      </div>
      <div className="grid gap-4 xl:grid-cols-2">
        <div className="space-y-4">
          <Panel className="space-y-3" data-testid="forms-base-panel">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="font-semibold">Standard questions</h3>
              <Select value={base.typeId} onValueChange={base.setTypeId}>
                <SelectTrigger className="w-[200px]" data-testid="forms-base-type-select"><SelectValue placeholder="Event type" /></SelectTrigger>
                <SelectContent>{base.types.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {base.current && <LockedQuestions eventType={base.current} />}
            <p className="text-xs text-muted-foreground">Set by ExpoEventHub. These can't be edited or removed, and they don't count toward your plan.</p>
          </Panel>
          {!cur ? <Panel className="text-sm text-muted-foreground">No extra questions yet. Create a set to ask attendees something specific to you.</Panel> : (
            <Panel className="space-y-3">
              <h3 className="font-semibold">Your questions</h3>
              <Input value={cur.name} onChange={(e) => setCur({ ...cur, name: e.target.value })} className="text-lg font-semibold" data-testid="form-name-input" />
              <QuestionsEditor fields={cur.fields} onChange={(fields) => setCur({ ...cur, fields })} />
              <div className="flex flex-wrap gap-2">
                <Button onClick={save} className="bg-primary text-white hover:bg-primary/90" data-testid="form-save-button">Save questions</Button>
                <Button variant="ghost" onClick={remove} className="text-rose-400" data-testid="form-delete-button">Delete</Button>
              </div>
            </Panel>
          )}
        </div>
        <div className="space-y-4 rounded-2xl border border-dashed border-border bg-muted/40 p-5" data-testid="form-preview">
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-emerald-600">Attendee preview</p>
          {base.current && <QuoteForm key={`b-${base.typeId}`} form={{ fields: base.current.questions }} preview testid="form-preview-base" />}
          {cur && (
            <>
              <p className="border-t pt-4 font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">Your questions</p>
              <QuoteForm key={JSON.stringify(cur.fields)} form={cur} preview />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
